import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity } from '../../../types/Activity';
import { activityService, ActivityWritePayload } from '../../../services/activityService';
import ActivityTable from '../components/ActivityTable';
import ActivityModal from '../components/ActivityModal';
import ActivityFilters, {
  ActivityFilterState,
  EMPTY_ACTIVITY_FILTERS,
  activityFiltersDiffer,
  countActivityFilters,
} from '../components/ActivityFilters';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { persistedListKey, usePersistedListState } from '../../../hooks/usePersistedListState';
import { Spinner, PageHeader, ListPage } from '@/components/ui';
import { confirmAction } from '@/components/ui';
import { LoadErrorState } from '@/components/ui';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT_FIELD = 'activityNumber';
/** sessionStorage page key for this page's remembered list state (V2-024). */
const LIST_KEY = 'activities';

type SortDirection = 'asc' | 'desc';

const ActivityView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The list state is kept for the sign-in (V2-024) and mirrored into the URL. A URL that
  // carries list state (a shared link, Back) wins over the remembered state: it is written into
  // the store once, before the persisted hooks below read it.
  useState(() => {
    if (![...searchParams.keys()].length) return;
    const p = parseInt(searchParams.get('page') || String(DEFAULT_PAGE), 10);
    const ps = parseInt(searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE), 10);
    const fromUrl: Record<string, unknown> = {
      filters: {
        classification: searchParams.get('classification') || '',
        profile: searchParams.get('profile') || '',
      },
      search: searchParams.get('search') || '',
      sort: {
        field: searchParams.get('sortField') || DEFAULT_SORT_FIELD,
        direction: searchParams.get('sortDirection') === 'desc' ? 'desc' : 'asc',
      },
      paging: {
        page: p >= 1 ? p : DEFAULT_PAGE,
        pageSize: PAGE_SIZE_OPTIONS.includes(ps) ? ps : DEFAULT_PAGE_SIZE,
      },
    };
    try {
      Object.entries(fromUrl).forEach(([name, value]) =>
        window.sessionStorage.setItem(persistedListKey(LIST_KEY, name), JSON.stringify(value))
      );
    } catch {
      // Storage unavailable: the hooks fall back to the defaults.
    }
    return true;
  });
  const [savedPaging, setSavedPaging] = usePersistedListState(LIST_KEY, 'paging', {
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | undefined>(undefined);
  const [profiles, setProfiles] = useState<string[]>([]);

  // Applied filters drive the request and the URL; pending filters are what the controls show.
  // Nothing refetches until Apply; Clear All resets both at once (W-M1). The search runs on its
  // own from the header-bar field (Enter), within the applied filters (SRCH-04).
  const [filters, setFilters] = usePersistedListState<ActivityFilterState>(LIST_KEY, 'filters', EMPTY_ACTIVITY_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<ActivityFilterState>(filters);
  const [search, setSearch] = usePersistedListState(LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(search);
  const [sort, setSort] = usePersistedListState<{ field: string; direction: SortDirection }>(LIST_KEY, 'sort', {
    field: DEFAULT_SORT_FIELD,
    direction: 'asc',
  });
  const [pagination, setPagination] = useState({
    page: savedPaging.page,
    pageSize: savedPaging.pageSize,
    totalCount: 0,
    totalPages: 0,
  });
  useEffect(() => {
    setSavedPaging({ page: pagination.page, pageSize: pagination.pageSize });
  }, [pagination.page, pagination.pageSize]);

  const { hasPermission } = usePermissions();

  // Load distinct profiles once (for the Profile filter dropdown).
  useEffect(() => {
    activityService
      .getProfileValues()
      .then(setProfiles)
      .catch(error => console.error('Error fetching profiles:', error));
  }, []);

  // Keep the URL query string in sync with the applied filter/search/sort/paging state.
  useEffect(() => {
    const params = new URLSearchParams();
    if (pagination.page !== DEFAULT_PAGE) params.set('page', String(pagination.page));
    if (pagination.pageSize !== DEFAULT_PAGE_SIZE) params.set('pageSize', String(pagination.pageSize));
    if (filters.classification) params.set('classification', filters.classification);
    if (filters.profile) params.set('profile', filters.profile);
    if (search) params.set('search', search);
    if (sort.field !== DEFAULT_SORT_FIELD) params.set('sortField', sort.field);
    if (sort.direction !== 'asc') params.set('sortDirection', sort.direction);
    setSearchParams(params, { replace: true });
  }, [
    pagination.page,
    pagination.pageSize,
    filters.classification,
    filters.profile,
    search,
    sort.field,
    sort.direction,
    setSearchParams,
  ]);

  // Fetch the current page from the server whenever any applied request input changes.
  const [loadFailed, setLoadFailed] = useState(false);
  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const res = await activityService.getActivitiesPaginated({
        classification: filters.classification || undefined,
        profile: filters.profile || undefined,
        search: search || undefined,
        sortField: sort.field,
        sortDirection: sort.direction,
        page: pagination.page,
        pageSize: pagination.pageSize,
      });
      setActivities(res.data);
      setPagination(prev => ({ ...prev, totalCount: res.totalCount, totalPages: res.totalPages }));
    } catch (error) {
      console.error('Error fetching activities:', error);
      // V2-020: a failed load is not "No activities match these filters".
      setLoadFailed(true);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [
    filters.classification,
    filters.profile,
    search,
    sort.field,
    sort.direction,
    pagination.page,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleAddActivity = () => {
    setSelectedActivity(undefined);
    setIsModalVisible(true);
  };

  const handleEditActivity = async (activity: Activity) => {
    try {
      // Fetch full activity details including all costings
      const fullActivity = await activityService.getActivityById(activity.id);
      setSelectedActivity(fullActivity);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error fetching activity details:', error);
      // Fallback to using the activity from the list
      setSelectedActivity(activity);
      setIsModalVisible(true);
    }
  };

  const handleSaveActivity = async (activityData: ActivityWritePayload) => {
    try {
      if (selectedActivity) {
        await activityService.updateActivity(selectedActivity.id, activityData);
      } else {
        await activityService.createActivity(activityData);
      }
      await fetchActivities();
      setIsModalVisible(false);
    } catch (error) {
      console.error('Error saving activity:', error);
      throw error;
    }
  };

  const handleDeleteActivity = async (id: number) => {
    if ((await confirmAction({ title: 'Delete Activity?', confirmLabel: 'Delete', destructive: true }))) {
      try {
        await activityService.deleteActivity(id);
        await fetchActivities();
      } catch (error) {
        console.error('Error deleting activity:', error);
      }
    }
  };

  const handleFilterChange = (name: keyof ActivityFilterState, value: string) => {
    setPendingFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handleSearch = (value: string) => {
    const next = value.trim();
    setSearchInput(next);
    setSearch(next);
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  // Clear All: the defaults, search included.
  const handleClearFilters = () => {
    setPendingFilters(EMPTY_ACTIVITY_FILTERS);
    setFilters(EMPTY_ACTIVITY_FILTERS);
    setSearchInput('');
    setSearch('');
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const hasUnappliedChanges = activityFiltersDiffer(pendingFilters, filters);
  const activeFilterCount = countActivityFilters(filters);

  const handleSortChange = (field: string) => {
    setSort(prev =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' }
    );
    setPagination(prev => ({ ...prev, page: DEFAULT_PAGE }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize, page: DEFAULT_PAGE }));
  };

  if (initialLoad) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center" style={{ minHeight: '200px' }}>
        <Spinner role="status">Loading...</Spinner>
      </div>
    );
  }

  return (
    <ListPage>
      <PageHeader
        title="Activities"
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', onClick: fetchActivities },
          {
            key: 'add',
            label: 'Add Activity',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !hasPermission('activity:create'),
            onClick: handleAddActivity,
          },
        ]}
      />

      <ActivityFilters
        filters={pendingFilters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        applyDisabled={!hasUnappliedChanges}
        onClearFilters={handleClearFilters}
        hasActiveFilters={activeFilterCount > 0 || search !== ''}
        activeFilterCount={activeFilterCount}
        profiles={profiles}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Number, name, or location code',
          label: 'Search activities',
        }}
      />

      <ListPage.Body>
        {loadFailed ? (
          <LoadErrorState what="activities" onRetry={() => void fetchActivities()} />
        ) : (
        <ActivityTable
          activities={activities}
          sortField={sort.field}
          sortDirection={sort.direction}
          onSort={handleSortChange}
          onEditActivity={handleEditActivity}
          onDeleteActivity={handleDeleteActivity}
        />
        )}
      </ListPage.Body>

      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        className="tw-mt-2"
      />

      <ActivityModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onSave={handleSaveActivity}
        activity={selectedActivity}
      />
    </ListPage>
  );
};

export default ActivityView;
